import type {
  ClientToServerMessage,
  ServerToClientMessage,
} from "@duelgame/shared";
import { SIGNALING_URL } from "../config";

type Handler = (msg: ServerToClientMessage) => void;

export class SignalingClient {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private openPromise: Promise<void> | null = null;

  onMessage(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.openPromise) return this.openPromise;

    this.openPromise = new Promise((resolve, reject) => {
      const ws = new WebSocket(SIGNALING_URL);
      this.ws = ws;

      ws.onopen = () => {
        this.openPromise = null;
        resolve();
      };

      ws.onerror = () => {
        this.openPromise = null;
        reject(new Error("Failed to connect to signaling server"));
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as ServerToClientMessage;
          for (const h of this.handlers) h(msg);
        } catch {
          /* ignore malformed */
        }
      };

      ws.onclose = () => {
        this.ws = null;
        this.openPromise = null;
      };
    });

    return this.openPromise;
  }

  send(msg: ClientToServerMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("Signaling socket not connected");
    }
    this.ws.send(JSON.stringify(msg));
  }

  create(): void {
    this.send({ type: "create" });
  }

  join(code: string): void {
    this.send({ type: "join", code });
  }

  queue(): void {
    this.send({ type: "queue" });
  }

  cancelQueue(): void {
    this.send({ type: "cancel_queue" });
  }

  leave(): void {
    this.send({ type: "leave" });
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
